/*
 * The one-off "Your company uses Slack. Get your notifications there?" bar — decision H3a of
 * DESIGN-slack.md, with Rob's answers of 13/10/2026: L1 (a bar at the bottom of whatever page
 * they land on) and M1 (Turn on ADDS Slack and keeps email).
 *
 * Mounted ONCE, in App.js beside the account-closing bar, so it rides every signed-in page —
 * including somebody arriving from a link in an email straight onto Approvals, which a
 * dashboard-only card would miss.
 *
 * ⚠️ **THE SERVER DECIDES WHETHER IT SHOWS** (`GET /profile/slack-offer`,
 * `services/channel_reach.should_offer`): only to people we've FOUND in their company's
 * Slack, who haven't got Slack on, and haven't answered. Offering it to somebody we can't
 * reach would be a promise we can't keep. Never during a support session.
 *
 * ⚠️ **ONCE ANSWERED, NEVER AGAIN — EITHER WAY.** It is an offer, not a nag. There is no ×:
 * closing it without answering would leave it to come back on the next page, which is worse.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { bottomDock } from '../lib/bottomDock';
import { Hash, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { profileApi, apiErrorMessage } from '../lib/api';
import { Button } from './ui/button';
import { CHANNELS_CHANGED } from './NotificationChannelCard';

export default function SlackOfferBar() {
  const { isAuthenticated, impersonation } = useAuth();
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!isAuthenticated || impersonation) { setOffer(null); return undefined; }
    profileApi.getSlackOffer()
      .then((r) => { if (alive) setOffer(r.data?.offer ? r.data : null); })
      .catch(() => { if (alive) setOffer(null); });
    return () => { alive = false; };
  }, [isAuthenticated, impersonation]);

  if (!offer) return null;

  const answer = async (yes) => {
    setBusy(yes ? 'yes' : 'no');
    try {
      const res = await profileApi.answerSlackOffer(yes ? 'yes' : 'no');
      setOffer(null);
      window.dispatchEvent(new Event(CHANNELS_CHANGED));
      if (yes) {
        const withEmail = (res.data?.channels || []).includes('email');
        toast.success(withEmail
          ? 'Slack is on. Your notifications will come to Slack as well as email. Untick email on your profile if you’d rather only have Slack.'
          : 'Slack is on. Your notifications will come to Slack too.');
      } else {
        toast('No problem. You can turn Slack on any time from your profile.');
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t save that. Please try again.'));
      // ⚠️ A refusal (we can't find them in Slack any more) means the offer is stale.
      if (e?.response?.status === 409) setOffer(null);
    } finally {
      setBusy(null);
    }
  };

  // ⚠️ Into the shared bottom dock (lib/bottomDock.js): an admin on a trial also has the
  // trial countdown there, and the two must stack rather than overlap.
  return createPortal(
    <div data-testid="slack-offer-bar" role="region" aria-label="Slack notifications">
      {/* A slim strip across the top since 29/10/2026 (lib/bottomDock.js) — it pushes the page down. */}
      <div className="border-b border-border bg-card px-4 py-1.5
                      flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
        <span className="w-6 h-6 shrink-0 rounded-md bg-amber-500/20 text-amber-600 flex items-center justify-center">
          <Hash className="w-4 h-4" aria-hidden="true" />
        </span>
        <div className="text-[13px] text-center">
          <span className="font-medium">Your company uses Slack.</span> Get your notifications there?{' '}
          <span className="text-xs text-muted-foreground">
            {offer.email_on ? 'We’ll still email you too. ' : ''}You can change this on your profile.
          </span>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" className="btn-primary h-7 px-3 text-xs" onClick={() => answer(true)} disabled={!!busy}
                  data-testid="slack-offer-yes">
            {busy === 'yes' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Turn on
          </Button>
          <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={() => answer(false)} disabled={!!busy}
                  data-testid="slack-offer-no">
            {busy === 'no' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            No thanks
          </Button>
        </div>
      </div>
    </div>,
    bottomDock(),
  );
}
